// stores/parentAuth.ts
import { reactive } from 'vue';
import { defineStore } from 'pinia';
import { useKeycloak, waitForKeycloakReady } from '@/composables/useKeycloak';
import { useParentStudents } from '@/composables/useParentStudents';
import type { components } from '@/api/api-types';

let ws: WebSocket | null = null;
type Student = components['schemas']['Student'];

export const useParentAuthStore = defineStore('parentAuth', () => {
  const state = reactive({
    api_host: import.meta.env.VITE_API_BASE_URL,
    ws_url: import.meta.env.VITE_WS_HOST,
    access_token: localStorage.getItem('parent_access_token') || '',
    refresh_token: localStorage.getItem('parent_refresh_token') || '',
    isAuthenticated: !!localStorage.getItem('parent_access_token'),
    userId: localStorage.getItem('parent_user_id') || '',
    email: localStorage.getItem('parent_email') || '',
    parentChildren: [] as Student[],
    isInitialized: false,
    child:{} as Student
  });

  async function initialize() {
    
    await waitForKeycloakReady();
    const { token } = useKeycloak();
    
    if (token.value) {
      setKeycloakToken(token.value);
    }
    
    state.access_token = localStorage.getItem('parent_access_token') || '';
    state.refresh_token = localStorage.getItem('parent_refresh_token') || '';
    state.isAuthenticated = !!state.access_token;
    state.email = localStorage.getItem('parent_email') || '';
    state.userId = localStorage.getItem('parent_user_id') || '';
    
    state.isInitialized = true;
    console.log('Parent auth initialized', state.isInitialized);
    
  }

  function decodeJWT(token: string) {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('❌ Invalid JWT format: expected 3 parts');
        return null;
      }

      if(parts[1]) {
        const payloadJson = atob(parts[1]);
      return JSON.parse(payloadJson);}
    } catch (error) {
      console.error('❌ Failed to decode JWT:', error);
      return null;
    }
  }


  function setKeycloakToken(token: string) {
    const payload = decodeJWT(token);
    if (!payload) {
      return;
    }

    const roles = payload.roles || [];
    const role = roles[1] || '';

    // if (role !== 'parent') {
    //   console.error('❌ User is not a parent');
    //   logout();
    //   return;
    // }

    state.access_token = token;
    state.isAuthenticated = true;

    localStorage.setItem('parent_access_token', token);

    state.userId = payload.user_id || payload.sub || '';
    state.email = payload.email || payload.preferred_username || '';

    if (state.email) localStorage.setItem('parent_email', state.email);
    if (state.userId) localStorage.setItem('parent_user_id', state.userId);

    state.isInitialized = true;
    
    // initWebSocket();
  }

  function setTokens(access: string, refresh: string) {
    const payload = decodeJWT(access);

    if (payload) {
      if (payload.sub || payload.user_id) {
        state.userId = payload.sub || payload.user_id || '';
        localStorage.setItem('parent_user_id', state.userId);
      }

      if (payload.email) {
        state.email = payload.email;
        localStorage.setItem('parent_email', payload.email);
      }
    }

    state.access_token = access;
    state.refresh_token = refresh;
    state.isAuthenticated = true;

    localStorage.setItem('parent_access_token', access);
    localStorage.setItem('parent_refresh_token', refresh);

    state.isInitialized = true;
    initWebSocket();
  }

  function setParentChildren(list: any[]) {
    state.parentChildren = list;
    
   
    localStorage.setItem('parent_children', JSON.stringify(list));
  }
  function setParentChild(child: Student) {
    state.child = child;
    
  }

  function logout() {
    state.access_token = '';
    state.refresh_token = '';
    state.isAuthenticated = false;
    state.parentChildren = [];

    localStorage.removeItem('parent_access_token');
    localStorage.removeItem('parent_refresh_token');
    localStorage.removeItem('parent_user_id');
    localStorage.removeItem('parent_email');

    if (ws) {
      ws.close();
      ws = null;
    }

  }

  function getFirstChild(): Student | undefined {
    console.log('get', state.parentChildren.length)
    return state.parentChildren.length > 0 ? state.parentChildren[0] : undefined;
  }

  function hasChildren(): boolean {
    return state.parentChildren.length > 0;
  }

  function setUserEmail(email: string) {
    state.email = email;
    localStorage.setItem('parent_email', email);
  }

  function clear() {
    logout();
  }

  function initWebSocket() {
    if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    if (!state.access_token) return;

    ws = new WebSocket(`${state.ws_url}?token=${state.access_token}`);
    ws.onopen = () => console.log('🔵 Parent WS connected');
    ws.onclose = (e) => console.log('⚪️ Parent WS closed', e.code, e.reason);
    ws.onerror = (e) => console.error('🔴 Parent WS error', e);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'ping') ws?.send(JSON.stringify({ type: 'pong' }));
        else console.log('Parent WS →', msg);
      } catch (err) {
        console.error('Parent WS message error', err);
      }
    };
  }

  async function refreshToken() {
    try {
      if (!state.refresh_token) {
        throw new Error('No refresh token available');
      }

      const res = await fetch(`${state.api_host}/parent/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: state.refresh_token }),
      });

      if (!res.ok) {
        throw new Error(`Refresh failed: ${res.status}`);
      }

      const { access_token, refresh_token } = await res.json();

      setTokens(access_token, refresh_token || state.refresh_token);

      return access_token;
    } catch (e: any) {
      console.error('❌ Parent token refresh failed:', e);
      logout();
      throw e;
    }
  }

  function isTokenExpired() {
    if (!state.access_token) return true;

    try {
      const payload = decodeJWT(state.access_token);
      if (!payload || !payload.exp) return true;

      const exp = payload.exp * 1000;
      return Date.now() >= exp - 60000;
    } catch {
      return true;
    }
  }

  function getUserId(): string | null {
    if (!state.access_token) return null;

    const payload = decodeJWT(state.access_token);
    if (!payload) return null;

    return payload.sub || payload.user_id || payload.id || null;
  }

  return {
    state,
    initialize,
    setTokens,
    setKeycloakToken,
    clear,
    logout,
    initWebSocket,
    setUserEmail,
    setParentChildren,
    setParentChild,
    refreshToken,
    isTokenExpired,
    getFirstChild,
    getUserId,
    decodeJWT,
  };
});